import React from 'react';
import { Button, StatusBadge } from '../../common';
import { Typography } from '../../common/Typography';

function sessionStatusVariant(status) {
  if (status === 'completed') return 'success';
  if (status === 'ongoing') return 'primary';
  if (status === 'upcoming') return 'info';
  if (status === 'cancelled') return 'danger';
  return 'default';
}

function formatSessionDate(dateStr) {
  const d = new Date(dateStr);
  return {
    short: d.toLocaleDateString('en-GB'),
    weekday: d.toLocaleDateString('en-GB', { weekday: 'long' }),
  };
}

/**
 * Recent sessions — card list on all breakpoints (no horizontal table scroll).
 */
export default function ClubAnalysisRecentSessions({ sessions = [], club, userRole, onViewSession }) {
  if (!sessions.length) {
    return (
      <div className="py-12 text-center">
        <div className="mb-4 text-4xl">📅</div>
        <Typography variant="body" className="mb-2 text-text-muted">
          No recent sessions found
        </Typography>
        <Typography variant="subtle" className="text-sm text-text-muted">
          Sessions will appear here once they are created
        </Typography>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {sessions.map((session) => {
        const dates = session.session_date ? formatSessionDate(session.session_date) : null;

        return (
          <div
            key={session.id || session.name}
            className="rounded-xl border border-border-medium bg-surface-subtle p-3 sm:p-4"
          >
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0 flex-1">
                <Typography variant="body" className="font-semibold text-text-primary">
                  {session.name || 'Untitled session'}
                </Typography>
                {session.description ? (
                  <Typography variant="subtle" className="mt-1 line-clamp-2 text-xs text-text-muted">
                    {session.description}
                  </Typography>
                ) : null}
                {dates ? (
                  <Typography variant="subtle" className="mt-2 text-xs text-text-secondary">
                    {dates.short} · {dates.weekday}
                  </Typography>
                ) : null}
              </div>
              <StatusBadge variant={sessionStatusVariant(session.session_status)} size="small" className="self-start">
                {session.session_status}
              </StatusBadge>
            </div>

            <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
              <div className="rounded-lg border border-border-light bg-surface px-3 py-2 text-center">
                <p className="text-lg font-bold tabular-nums text-text-primary">{session.player_count || 0}</p>
                <p className="text-[10px] uppercase tracking-wide text-text-muted">Players</p>
              </div>
              <div className="rounded-lg border border-border-light bg-surface px-3 py-2 text-center">
                <p className="text-lg font-bold tabular-nums text-text-primary">{session.game_count || 0}</p>
                <p className="text-[10px] uppercase tracking-wide text-text-muted">Games</p>
              </div>
              {session.id && club?.id ? (
                <div className="col-span-2 sm:col-span-1 sm:flex sm:items-stretch">
                  <Button
                    type="button"
                    variant="outline"
                    size="small"
                    className="h-full w-full"
                    onClick={() => onViewSession?.(session)}
                  >
                    View session
                  </Button>
                </div>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}
